import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { RichTextEditor } from "../../components/RichTextEditor";
import { api } from "../../services/api";
import { GrupoTextoSite } from "../../types/grupoTextoSite";
import { Grupo } from "../../types/grupos";
import { ArrowLeft, Save, Eye, EyeOff, Globe, FileText } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";

export function GrupoTextoSitePage() {
  const { grupoNome } = useParams<{ grupoNome: string }>();
  const navigate = useNavigate();
  const [grupo, setGrupo] = useState<Grupo | null>(null);
  const [resolvedGrupoId, setResolvedGrupoId] = useState<string | null>(null);
  const [textoSite, setTextoSite] = useState<GrupoTextoSite | null>(null);
  const [conteudoHtml, setConteudoHtml] = useState("");
  const [publicado, setPublicado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (grupoNome) {
      loadGrupo();
    }
  }, [grupoNome]);

  useEffect(() => {
    if (resolvedGrupoId) {
      loadTextoSite();
    }
  }, [resolvedGrupoId]);

  const loadGrupo = async () => {
    try {
      const response = await api.get(`/grupos/nome/${grupoNome}`);
      setGrupo(response.data);
      setResolvedGrupoId(response.data.id);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar dados do grupo", { variant: "error" });
    }
  };

  const loadTextoSite = async () => {
    if (!resolvedGrupoId) return;
    try {
      setLoading(true);
      const response = await api.get(`/grupos/${resolvedGrupoId}/texto-site`);
      setTextoSite(response.data);
      setConteudoHtml(response.data.conteudo_html || "");
      setPublicado(response.data.publicado || false);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        enqueueSnackbar("Erro ao carregar texto do site", { variant: "error" });
      }
      setConteudoHtml("");
      setPublicado(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!resolvedGrupoId) return;

    try {
      setSaving(true);
      await api.post(`/grupos/${resolvedGrupoId}/texto-site`, {
        conteudo_html: conteudoHtml,
        publicado
      });

      enqueueSnackbar("Texto do site salvo com sucesso!", { variant: "success" });
      loadTextoSite();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar texto do site",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!grupo) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/grupos/${grupoNome}`)}
            className="flex items-center gap-2 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-[oklch(0.45_0.15_142)] shrink-0" />
              <span className="truncate">Texto para Landing Page</span>
            </h1>
            <p className="text-muted-foreground mt-1 text-sm truncate">{grupo.grupo}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:shrink-0">
          <div className="flex items-center space-x-2">
            <Switch
              id="publicado"
              checked={publicado}
              onCheckedChange={setPublicado}
            />
            <Label htmlFor="publicado" className="flex items-center gap-2">
              {publicado ? (
                <>
                  <Eye className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">Publicado</span>
                </>
              ) : (
                <>
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Rascunho</span>
                </>
              )}
            </Label>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white w-full sm:w-auto"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>

      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-[oklch(0.35_0.15_142)] flex items-center gap-2 text-base">
            <FileText className="w-5 h-5" />
            Sobre o Editor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Este editor permite criar o conteúdo HTML que será exibido na landing page do grupo.
            O conteúdo aqui inserido aparecerá na página pública do grupo quando a landing page for implementada.
          </p>
          {textoSite && (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>Última atualização: {new Date(textoSite.alterado_em).toLocaleString('pt-BR')}</span>
              {textoSite.publicado_em && (
                <span>Publicado em: {new Date(textoSite.publicado_em).toLocaleString('pt-BR')}</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[oklch(0.35_0.15_142)]">Conteúdo</CardTitle>
          <CardDescription>
            Crie o conteúdo que será exibido na landing page do grupo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-gray-500">Carregando editor...</div>
            </div>
          ) : (
            <RichTextEditor
              value={conteudoHtml}
              onChange={setConteudoHtml}
              placeholder="Digite o conteúdo da landing page do grupo..."
              height="500px"
              enableImages
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
